import {useState, useRef, useEffect} from "react";
import {
  AUDIO_CONTEXT_ERROR_CODES,
  AudioContextError,
} from "./audioContext.error";
import {RecordingState} from "./audioContext.type";

const SILENT_COMPRESSION = {
  THRESHOLD: 200, // 무음 판단 기준 임계값
  FRAME_SIZE: 160, // 무음 판단 시간 해상도 (10ms @ 16kHz)
  MAX_SILENT_FRAMES: 10, // 유지할 무음 프레임 수
  KEEP_EDGE_FRAMES: 15, // 앞/뒤 무음 보호 프레임 수 (안정성 증가)
} as const;

interface UseAudioContextReturn {
  audioState: RecordingState;
  startRecording: () => Promise<void>;
  stopRecording: () => Float32Array | null;
  convertToUint8Array: (
    audioData: Float32Array,
    isRemoveSilent?: boolean,
  ) => Uint8Array | null;
}

export const useAudioContext = (): UseAudioContextReturn => {
  const [audioState, setAudioState] = useState<RecordingState>("idle");

  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const pcmBufferRef = useRef<Float32Array[]>([]);

  const checkMicrophonePermission = async (): Promise<
    "granted" | "denied" | "prompt"
  > => {
    if (!navigator.permissions) {
      return "prompt";
    }

    try {
      const result = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });
      return result.state as "granted" | "denied" | "prompt";
    } catch (error) {
      return "prompt";
    }
  };

  const requestMicrophoneAccess = async (): Promise<MediaStream> => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new AudioContextError(
        AUDIO_CONTEXT_ERROR_CODES.UNSUPPORTED_BROWSER,
        "이 브라우저는 마이크 기능을 지원하지 않습니다.",
      );
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      return stream;
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "NotAllowedError") {
          throw new AudioContextError(
            AUDIO_CONTEXT_ERROR_CODES.NOT_ALLOWED,
            "마이크 접근 권한을 허용해주세요!",
          );
        }
        if (error.name === "NotFoundError") {
          throw new AudioContextError(
            AUDIO_CONTEXT_ERROR_CODES.NOT_FOUND,
            "마이크를 찾을 수 없습니다.",
          );
        }
        if (
          error.name !== "NotAllowedError" &&
          error.name !== "NotFoundError"
        ) {
          throw new AudioContextError(
            AUDIO_CONTEXT_ERROR_CODES.MIC_ACCESS_ERROR,
            "마이크 접근 중 오류가 발생했습니다.",
          );
        }
      }
      throw new AudioContextError(
        AUDIO_CONTEXT_ERROR_CODES.MIC_ACCESS_ERROR,
        "마이크 접근 중 오류가 발생했습니다.",
      );
    }
  };

  const compressSilentFrames = (samples: Int16Array): Int16Array => {
    if (samples.length === 0) {
      return new Int16Array([]);
    }

    const result: number[] = [];

    const isSilentFrame = (frame: Int16Array): boolean => {
      for (let i = 0; i < frame.length; i++) {
        if (Math.abs(frame[i]) > SILENT_COMPRESSION.THRESHOLD) {
          return false;
        }
      }
      return true;
    };

    let silentCount = 0;
    const totalFrames = Math.ceil(
      samples.length / SILENT_COMPRESSION.FRAME_SIZE,
    );

    for (let i = 0; i < totalFrames; i++) {
      const start = i * SILENT_COMPRESSION.FRAME_SIZE;
      const end = Math.min(
        start + SILENT_COMPRESSION.FRAME_SIZE,
        samples.length,
      );
      const frame = samples.subarray(start, end);

      const isSilent = isSilentFrame(frame);

      // 앞 / 뒤 무음 보호
      if (
        i < SILENT_COMPRESSION.KEEP_EDGE_FRAMES ||
        i >= totalFrames - SILENT_COMPRESSION.KEEP_EDGE_FRAMES
      ) {
        result.push(...frame);
        continue;
      }
      // 음성 프레임
      if (!isSilent) {
        silentCount = 0;
        result.push(...frame);
      }
      // 유지할 무음 프레임
      if (isSilent && silentCount < SILENT_COMPRESSION.MAX_SILENT_FRAMES) {
        result.push(...frame);
        silentCount++;
      }
    }

    return new Int16Array(result);
  };

  const convertToUint8Array = (
    audioData: Float32Array,
    isRemoveSilent: boolean = false,
  ): Uint8Array | null => {
    // AudioContext를 통해 수집한 오디오 데이터(Float32Array)를
    // PCM 기준인 Int16Array로 변환하고,
    // 서버로 전송하기 위해 Uint8Array로 변환
    if (!audioData || audioData.length === 0) {
      return null;
    }

    const int16Array = new Int16Array(audioData.length);

    for (let i = 0; i < audioData.length; i++) {
      const sample = audioData[i];

      const clamped = Math.max(-1, Math.min(1, sample));
      int16Array[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
    }

    if (isRemoveSilent) {
      const processedInt16Array = compressSilentFrames(int16Array);
      const uint8Array = new Uint8Array(processedInt16Array.buffer);
      return uint8Array;
    }

    const uint8Array = new Uint8Array(int16Array.buffer);
    return uint8Array;
  };

  const startRecording = async () => {
    if (audioState === "recording") {
      return;
    }

    if (!window.AudioContext && !(window as any).webkitAudioContext) {
      throw new AudioContextError(
        AUDIO_CONTEXT_ERROR_CODES.UNSUPPORTED_BROWSER,
        "이 브라우저는 AudioContext를 지원하지 않습니다.",
      );
    }

    const AudioContextClass =
      window.AudioContext || (window as any).webkitAudioContext;
    const audioContext = new AudioContextClass({sampleRate: 16000});

    if (!audioContext.audioWorklet) {
      throw new AudioContextError(
        AUDIO_CONTEXT_ERROR_CODES.UNSUPPORTED_AUDIO_WORKLET,
        "이 브라우저는 AudioWorklet을 지원하지 않습니다.",
      );
    }

    const permission = await checkMicrophonePermission();
    if (permission === "denied") {
      throw new AudioContextError(
        AUDIO_CONTEXT_ERROR_CODES.NOT_ALLOWED,
        "마이크 접근 권한을 허용해주세요!",
      );
    }

    const stream = await requestMicrophoneAccess();

    try {
      audioContextRef.current = audioContext;

      await audioContext.audioWorklet.addModule("/audio-processor.js");

      const sourceNode = audioContext.createMediaStreamSource(stream);
      sourceNodeRef.current = sourceNode;

      const workletNode = new AudioWorkletNode(
        audioContext,
        "audio-recorder-processor",
      );
      workletNodeRef.current = workletNode;

      pcmBufferRef.current = [];

      workletNode.port.onmessage = (event) => {
        if (event.data.type === "audioData") {
          pcmBufferRef.current.push(event.data.data);
        }
      };

      sourceNode.connect(workletNode);
      workletNode.connect(audioContext.destination);

      streamRef.current = stream;
      setAudioState("recording");
    } catch (error) {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      setAudioState("idle");
      throw new AudioContextError(
        AUDIO_CONTEXT_ERROR_CODES.RECORDING_START_FAILED,
        "녹음을 시작할 수 없습니다.",
      );
    }
  };

  const stopRecording = (): Float32Array | null => {
    if (audioState !== "recording") {
      return null;
    }

    let mergedPcm: Float32Array | null = null;

    setAudioState("processing");

    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (pcmBufferRef.current.length > 0) {
      const totalLength = pcmBufferRef.current.reduce(
        (sum, buffer) => sum + buffer.length,
        0,
      );
      mergedPcm = new Float32Array(totalLength);
      let offset = 0;

      for (const buffer of pcmBufferRef.current) {
        mergedPcm.set(buffer, offset);
        offset += buffer.length;
      }
    }

    setAudioState("idle");
    return mergedPcm;
  };

  useEffect(() => {
    return () => {
      if (workletNodeRef.current) {
        workletNodeRef.current.disconnect();
        workletNodeRef.current = null;
      }

      if (sourceNodeRef.current) {
        sourceNodeRef.current.disconnect();
        sourceNodeRef.current = null;
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      if (audioContextRef.current) {
        audioContextRef.current.close().catch(console.error);
        audioContextRef.current = null;
      }
    };
  }, []);

  return {
    audioState,
    startRecording,
    stopRecording,
    convertToUint8Array,
  };
};
